import type { FetchedWasteTypes } from "@/Utils/api/sanity/types";
import { formatNumberWithDecimalPlaces } from "@/Utils/formatFunctions";
import type { WasteTypeConfig } from "@/pages/configuration/useConfigService";
import {
  COLORS,
  getNewColor,
} from "@/pages/customer/dashboard/functions/pieChartData";

import { sortBy, sum } from "lodash";
import type { Terminal } from "server/dwhApi/terminals/types.js";

import type { DistributionData } from "./types";

const MAX_LEGEND_WASTE_TYPES = 8;

export const getWasteTypeDistributionData = (
  realEstate: Terminal | undefined,
  wasteTypes: FetchedWasteTypes | null,
  translationTextUnknown: string,
  translationTextOther: string,
  wasteTypeConfig: WasteTypeConfig[],
  otherColor: string,
): DistributionData[] => {
  if (!realEstate?.wasteTypes || !wasteTypes) return [];

  let curColors = [...COLORS];

  const sortedWasteTypes = sortBy(
    realEstate.wasteTypes,
    "weight.quantity",
  ).reverse();

  const nrDisplayedWasteTypes =
    sortedWasteTypes.length > MAX_LEGEND_WASTE_TYPES
      ? MAX_LEGEND_WASTE_TYPES - 1
      : MAX_LEGEND_WASTE_TYPES;

  const displayedWasteTypes = sortedWasteTypes.slice(0, nrDisplayedWasteTypes);

  const data = displayedWasteTypes
    .map((wt) => {
      const [color, newColors] = getNewColor(curColors);
      curColors = newColors;

      const customWasteType = wasteTypeConfig.find(
        (wtc) => wtc.wasteTypeCode === wt.code,
      );
      const wasteTypeName =
        customWasteType?.name ||
        wasteTypes.find((wasteType) => wasteType.id === wt.code)?.name ||
        `${translationTextUnknown} (${wt.code})`;

      const sanityDefaultColor = wasteTypes?.find(
        (swt) => swt.id === wt.code,
      )?.wasteTypeDefaultColor;

      return {
        id: wasteTypeName,
        code: wt.code,
        label: wasteTypeName,
        value: formatNumberWithDecimalPlaces(wt.weight.quantity, 1),
        color: customWasteType?.color || sanityDefaultColor || color,
      };
    })
    .map((el, i) => ({ ...el, id: `${el.id}-${i}` })) // added to mitigate cases where waste types have the same name
    .filter((el) => el.value !== 0);

  if (sortedWasteTypes.length > MAX_LEGEND_WASTE_TYPES) {
    const otherWasteTypes = sortedWasteTypes.slice(nrDisplayedWasteTypes);

    const otherValue = sum(otherWasteTypes.map((wt) => wt.weight.quantity));

    data.push({
      id: "genericLabels:other",
      code: "",
      label: translationTextOther,
      value: formatNumberWithDecimalPlaces(otherValue, 1),
      color: otherColor,
    });
  }

  return data;
};
